/**
 * 故障状态 Store（Zustand）
 * 管理故障事件列表、当前故障和故障统计信息
 */
import { create } from 'zustand';
import type { FaultEvent, FaultLogEntry } from '@fluxlab/protocols';

interface FaultStore {
  /** Real-time faults pushed from WebSocket */
  realtimeFaults: FaultEvent[];
  /** Historical faults loaded from backend */
  historyFaults: FaultLogEntry[];
  /** Total counts by severity */
  counts: {
    critical: number;
    error: number;
    warning: number;
    info: number;
  };

  addRealtimeFault: (fault: FaultEvent) => void;
  setHistoryFaults: (faults: FaultLogEntry[]) => void;
  clearRealtimeFaults: () => void;
}

function computeCounts(faults: FaultEvent[]): FaultStore['counts'] {
  return {
    critical: faults.filter((f) => f.severity === 'critical').length,
    error: faults.filter((f) => f.severity === 'error').length,
    warning: faults.filter((f) => f.severity === 'warning').length,
    info: faults.filter((f) => f.severity === 'info').length,
  };
}

/** 故障状态 Store（Zustand）：管理故障事件列表、当前故障与统计信息 */
export const useFaultStore = create<FaultStore>((set) => ({
  realtimeFaults: [],
  historyFaults: [],
  counts: { critical: 0, error: 0, warning: 0, info: 0 },

  addRealtimeFault: (fault) =>
    set((s) => {
      const updated = [fault, ...s.realtimeFaults].slice(0, 500); // cap at 500
      return { realtimeFaults: updated, counts: computeCounts(updated) };
    }),

  setHistoryFaults: (faults) =>
    set({
      historyFaults: faults,
      counts: computeCounts(
        faults.map((f) => ({
          code: f.code,
          severity: f.severity as FaultEvent['severity'],
          message: f.message,
          timestamp: f.timestamp,
        })),
      ),
    }),

  clearRealtimeFaults: () =>
    set({ realtimeFaults: [], counts: { critical: 0, error: 0, warning: 0, info: 0 } }),
}));
